import React from 'react';
import { C, S, deltaTone } from './_overviewTheme';

/**
 * ⛔⛔ THREE STATES, NEVER TWO — Card P145, the #686 rule applied to a KPI.
 *
 * A card shows a NUMBER, "loading", or "not tracked yet" with the reason it
 * is not tracked. It never shows 0 for an absent figure.
 *
 * ⚠⚠ This is the whole reason the screen can ship before its data does. The
 * 28 Oct live dashboard showed "Therapies scheduled 0", "Lead → Therapy 0%"
 * and "60-day forecast $0" — three figures that were not zero, they were
 * broken, and a reader had no way to tell the difference. A zero is a claim
 * about the business; "not tracked yet" is a claim about the system, and
 * only one of them was true.
 */
/*
 * ⚠ `untrackedLabel` — Card P254. The headline an untracked card shows in
 * place of a figure. It DEFAULTS to "Not tracked yet", so every existing
 * caller renders exactly what it rendered before.
 *
 * It exists because the goal tracker's three undefined tiles (Deposits
 * received, Consultations completed, Therapies completed) must say
 * "Definition needed", not "Not tracked yet". Those are different claims:
 * "not tracked yet" says the SYSTEM cannot see the figure, which invites
 * someone to go and fix the pipeline; "definition needed" says nobody has
 * decided WHAT the figure is, and the fix is a conversation with Carlos. The
 * reader's next action differs, so the words have to.
 *
 * ⛔ What does NOT change is that neither renders a number. The prop moves
 * the wording, never the state machine.
 */
export function StateBody({ state, reason, children, small, untrackedLabel = 'Not tracked yet' }) {
  if (state === 'loading') {
    return <div style={{ fontSize: small ? 12 : 14, color: C.faint }}>Loading…</div>;
  }
  if (state === 'untracked') {
    return (
      <div>
        <div style={{ fontSize: small ? 12 : 13, fontWeight: 600, color: C.muted }}>
          {untrackedLabel}
        </div>
        {reason ? <div style={{ ...S.kpiSub, marginTop: 4 }}>{reason}</div> : null}
      </div>
    );
  }
  if (state === 'failed') {
    return (
      <div>
        <div style={{ fontSize: small ? 12 : 13, fontWeight: 600, color: C.high }}>
          Could not load
        </div>
        {reason ? <div style={{ ...S.kpiSub, marginTop: 4, color: C.watchDeep }}>{reason}</div> : null}
      </div>
    );
  }
  return children;
}

/**
 * One KPI card.
 *
 * ⚠ `target` renders NOTHING when absent — not a dash, not a grey bar, not a
 * zero. Carlos has not set targets; inventing one so the layout looks even
 * would put a number on an executive screen that nobody decided.
 */
export default function OverviewCard({
  label, state = 'ok', reason, value, deltaPct, deltaLabel = 'vs previous',
  higherIsBetter = true, sub, target, testid, onOpen = null,
  /* Card P254 — see StateBody above. Default keeps every existing caller's
     wording identical. */
  untrackedLabel = 'Not tracked yet',
}) {
  const showDelta = state === 'ok' && deltaPct !== null && deltaPct !== undefined && Number.isFinite(deltaPct);

  /*
   * ⛔⛔ A REAL <button> WHEN IT IS CLICKABLE — Card P167.
   *
   * The headline tiles were not drill-down entry points at all: no handler,
   * no affordance. Daniel's ruling makes every figure openable, and the
   * repo's standing rule is that an interactive element is a real control,
   * never onClick on a <div> or <section> — Tab skips those, and a screen
   * reader never announces them.
   *
   * ⚠ A tile with no target stays a plain <section>: an affordance that
   * promises a destination and does nothing is the defect P153 removed from
   * this screen, and adding a dead one here would reintroduce it.
   */
  const Wrapper = onOpen ? 'button' : 'section';
  const wrapperProps = onOpen
    ? { type: 'button', onClick: onOpen,
        style: { ...S.card, textAlign: 'left', cursor: 'pointer', font: 'inherit', width: '100%' } }
    : { style: S.card };

  return (
    <Wrapper {...wrapperProps} data-testid={testid}>
      <div style={S.kpiLabel}>{label}</div>
      <StateBody state={state} reason={reason} untrackedLabel={untrackedLabel}>
        <>
          <div style={S.kpiValue} data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
          {showDelta ? (
            <div
              data-testid={testid ? `${testid}-delta` : undefined}
              style={{ fontSize: 12, fontWeight: 600, marginTop: 6, color: deltaTone(deltaPct, higherIsBetter) }}
            >
              {deltaPct > 0 ? '▲' : deltaPct < 0 ? '▼' : '■'} {Math.abs(deltaPct).toFixed(1)}% {deltaLabel}
            </div>
          ) : null}
          {target ? (
            <div data-testid={testid ? `${testid}-target` : undefined}
                 style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              {target}
            </div>
          ) : null}
          {sub ? <div style={S.kpiSub}>{sub}</div> : null}
        </>
      </StateBody>
    </Wrapper>
  );
}
